import React from 'react';
import cn from 'classnames';

import { PRICE } from 'common/regex';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { AFTERPAY_MAXIMUM, AFTERPAY_MINIMUM } from 'constants/appConstants';
import ProductUtils from 'helpers/ProductUtils';
import useMartyContext from 'hooks/useMartyContext';
import { ProductStyle } from 'types/cloudCatalog';
import { IdentityFn } from 'types/utility';
import FinalSale from 'components/common/FinalSale';

import css from 'styles/components/productdetail/price.scss';

interface Props {
  productStyle: ProductStyle;
  percentOffText: string;
  showPercentOffBanner: boolean;
  className?: string;
  small?: boolean;
  defaultProductType?: string;
  isAvailable?: boolean;
  hydraPercentOffPlacement?: number;
  isLowestRecentPrice?: boolean;
}

function getPriceComponents(priceString: string): string[] | undefined {
  const match = priceString.match(PRICE);
  if (!match) {
    return undefined;
  }
  if (!match[2]) {
    return [match[1]!];
  }
  return [match[1]!, match[2]];
}

const makePercentOff = (style: ProductStyle, percentOffText: string, asPercentSavings: boolean, testId: IdentityFn<string>) => {
  const percentOff = ProductUtils.getPercentOff(style);
  if (percentOff > 0) {
    if (asPercentSavings) {
      return (
        <span className={css.percentOffText} data-test-id={testId('percentOff')}>
          -{percentOff}%
        </span>
      );
    }

    return (
      <span data-test-id={testId('percentOff')}>
        {percentOff}% {percentOffText}
      </span>
    );
  } else {
    return;
  }
};

export const Price = ({
  className,
  percentOffText,
  productStyle,
  showPercentOffBanner,
  small,
  defaultProductType,
  isAvailable,
  hydraPercentOffPlacement = 0,
  isLowestRecentPrice
}: Props) => {
  const {
    testId,
    marketplace: {
      checkout: { allowAfterPay }
    }
  } = useMartyContext();
  const { productUrl, finalSale, price, originalPrice } = productStyle;
  const showDiscount = ProductUtils.isStyleOnSale(productStyle);
  const percentOff = makePercentOff(productStyle, percentOffText, hydraPercentOffPlacement === 1, testId);
  const mainPrice = price || originalPrice;
  const components = getPriceComponents(mainPrice);
  const dollarSign = components ? '$' : '';
  const priceDollarValue = components ? components[0] : mainPrice;
  const priceCentsValue = components?.[1] ? components[1] : '';
  const structuredDataPrice = priceDollarValue + (priceCentsValue ? `.${priceCentsValue}` : '');
  const isProductAfterpayEligible =
    defaultProductType !== 'Gift Cards' && +priceDollarValue! >= AFTERPAY_MINIMUM && +priceDollarValue! <= AFTERPAY_MAXIMUM;

  return (
    <div className={cn(className, { [css.small]: small }, css.main)}>
      <div className={css.container} itemProp="offers" itemScope itemType="https://schema.org/Offer" data-test-id={testId('priceOffersContainer')}>
        {hydraPercentOffPlacement === 1 && <span className={cn(css.price)}>{percentOff}</span>}
        {/* aria-label tells price to screen reader */}
        <span
          aria-label={mainPrice}
          className={cn(css.price, { [css.sale]: showDiscount, [css.priceColorOverride]: showDiscount && hydraPercentOffPlacement > 0 })}
          data-test-id={testId('pdpProductPrice')}
          itemProp="price"
          content={structuredDataPrice}
        >
          {/*
            hide the fancy-formatted price (superscript dollar sign and cents)
            because the styling alone causes it to be broken up. a screen
            reader will see "$\n24\n.\n99" for a price of "$24.99"
          */}
          <span className={css.priceText} aria-hidden="true">
            {/* TODO refactor all this into a StylizedPrice component (working name) */}
            <span className={cn(css.dollarSign, css.priceText)} itemProp="priceCurrency" content="USD">
              {dollarSign}
            </span>
            {priceDollarValue}
            {priceCentsValue && (
              <>
                <span className={cn(css.priceDecimalPoint, css.priceText)}>.</span>
                <span className={cn(css.priceCentsValue, css.priceText)}>{priceCentsValue}</span>
              </>
            )}
          </span>
        </span>
        {showDiscount && (
          <span className={cn(css.discount, { [css.discountOverride]: hydraPercentOffPlacement === 1 })}>
            {percentOff && <span className={css.percentOff}>{percentOff}</span>}
            <span className={css.originalPrice} data-test-id={testId('pdpOriginalPrice')}>
              {showDiscount && (
                <span className={css.msrpLabel}>
                  <abbr title="Manufacturer's Suggested Retail Price">MSRP</abbr>:{' '}
                </span>
              )}
              {hydraPercentOffPlacement === 1 && <span className={css.msrpText}>List Price: </span>}
              <span className={cn(css.msrpText, css.lineThrough)} data-test-id={testId('msrpPrice')}>
                {originalPrice}
              </span>
            </span>
          </span>
        )}
        {/* if this treatment goes permanent just turn showPercentOffBanner flag to true for zappos. */}
        {(showPercentOffBanner || hydraPercentOffPlacement === 2) && <span className={css.percentOffBanner}>{percentOff}</span>}
        {finalSale && <FinalSale className={css.finalSale} />}
        {showDiscount && isLowestRecentPrice && <div className={css.recentLowestPrice}>Lowest Price in 30 days</div>}
        {isAvailable ? (
          <meta itemProp="availability" content="https://schema.org/InStock" />
        ) : (
          <meta itemProp="availability" content="https://schema.org/OutOfStock" />
        )}
        <meta itemProp="url" content={productUrl} />
      </div>

      {!!components && allowAfterPay && (
        <div className={css.afterpayPlacement} data-test-id={testId('afterpay')}>
          <afterpay-placement
            data-locale="en_US"
            data-currency="USD"
            data-is-eligible={isProductAfterpayEligible}
            data-amount={`${priceDollarValue}${priceCentsValue && '.'}${priceCentsValue}`}
            data-logo-type="lockup"
            data-size="sm"
            data-modal-theme="white"
          />
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary('Price', Price);
